// Gallery.jsx
import React, { useState, useEffect } from "react";
import Masonry from "react-masonry-css";
import { motion, AnimatePresence } from "framer-motion";
import "./Gallery.css";
import GalleryItem from "./GalleryItem";

const Gallery = ({ images }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [columns, setColumns] = useState({
    default: 3,
    1100: 2,
    700: 2,
    500: 1,
  });

  // Adjust columns based on screen width for better responsiveness
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width > 1400) {
        setColumns({
          default: 3,
          1400: 3,
          1100: 2,
          700: 2,
          500: 1,
        });
      } else if (width > 1100) {
        setColumns({
          default: 3,
          1100: 2,
          700: 2,
          500: 1,
        });
      } else {
        setColumns({
          default: 2,
          700: 2,
          500: 1,
        });
      }
    };

    // Set initial columns
    handleResize();
    // Add event listener
    window.addEventListener("resize", handleResize);
    // Clean up
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const openModal = (image) => {
    setSelectedImage(image);
    // Prevent body scrolling when modal is open
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setSelectedImage(null);
    // Re-enable scrolling
    document.body.style.overflow = "auto";
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27 && selectedImage) {
        closeModal();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [selectedImage]);

  // Sort images alphabetically and remove duplicates
  const sortedUniqueImages = React.useMemo(() => {
    if (!images || !Array.isArray(images)) return [];

    // Remove duplicates based on src
    const uniqueImages = images.filter(
      (image, index, self) =>
        index === self.findIndex((img) => img.src === image.src)
    );

    // Sort alphabetically by filename (extracted from alt or src)
    return uniqueImages.sort((a, b) => {
      const nameA = (a.alt || a.src.split("/").pop()).toLowerCase();
      const nameB = (b.alt || b.src.split("/").pop()).toLowerCase();
      return nameA.localeCompare(nameB, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });
  }, [images]);

  return (
    <div className="gallery-container">
      <Masonry
        breakpointCols={columns}
        className="my-masonry-grid"
        columnClassName="my-masonry-grid_column"
      >
        {sortedUniqueImages.map((image, index) => (
          <GalleryItem
            key={`${image.src}-${index}`} // Better key using src + index
            image={image}
            index={index}
            openModal={openModal}
          />
        ))}
      </Masonry>
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="image-modal"
            onClick={closeModal}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 15 }}
            >
              <span className="close-button" onClick={closeModal}>
                &times;
              </span>
              <div className="modal-image-container">
                <img
                  src={selectedImage.src}
                  alt={selectedImage.alt || "Gallery image"}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
